import { Box, Button, Flex, Heading, Text } from "@radix-ui/themes";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { DashboardWidget } from "@/types/breeze/Dashboard";

interface Props {
  widget: DashboardWidget;
}

interface GettingStartedAction {
  label: string;
  route?: string;
  url?: string;
}

interface GettingStartedConfig {
  body?: string;
  actions?: GettingStartedAction[];
}

const DEFAULT_BODY =
  "Start by creating your first data model to define the structure of your application data. Then explore our payment integration with AnythinkPay to handle transactions seamlessly.";

const DEFAULT_ACTIONS: GettingStartedAction[] = [
  { label: "Create Data Model", route: "/settings/data-model" },
  { label: "View Anythink Docs", url: "https://docs.anythink.cloud" },
];

export function GettingStartedWidget({ widget }: Props) {
  const { safeNavigate } = useAuth();
  const config = parseConfig(widget.config_json);
  const body = config?.body ?? DEFAULT_BODY;
  const actions = config?.actions?.length ? config.actions : DEFAULT_ACTIONS;

  const openAction = (action: GettingStartedAction) => {
    if (action.url) {
      const normalized = /^[a-z][a-z0-9+.-]*:\/\//i.test(action.url)
        ? action.url
        : `https://${action.url}`;
      window.open(normalized, "_blank", "noopener,noreferrer");
    } else if (action.route) {
      safeNavigate(action.route);
    }
  };

  return (
    <Box p="5" style={{ height: "100%", overflow: "auto" }}>
      <Heading size="3" weight="medium" mb="3">
        {widget.title}
      </Heading>
      <Text color="gray" size="2" mb="4" asChild>
        <div>{body}</div>
      </Text>
      <Flex gap="3" wrap="wrap">
        {actions.map((action, i) => {
          const isExternal = !!action.url;
          return (
            <Button
              key={i}
              variant={i === 0 ? "solid" : "outline"}
              onClick={() => openAction(action)}
              disabled={!action.route && !action.url}
            >
              {action.label}
              {isExternal && <ExternalLink size={14} />}
            </Button>
          );
        })}
      </Flex>
    </Box>
  );
}

function parseConfig(json: string | null): GettingStartedConfig | null {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}
